import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import PhonePreview from 'components/PhonePreview/PhonePreview';
import { actions as aiActions } from 'coupon-common/src/modules/aiContent';
import { dispatchAsAsync } from 'coupon-common/src/utils';

import { descriptionPrompt,titlePrompt } from '../../coupon-ai/prompts';
import styles from './SignupCouponBuilder.module.scss';
import Step1_CombinedSetup from './Step1_CombinedSetup';
import Step2_LoadingRecommendations from './Step2_LoadingRecommendations';
import Step3_FinalReview from './Step3_FinalReview';
import { loadJson, removeKey, saveJson, STORAGE_KEYS } from './utils/storage';

const GOAL_LABELS = {
  attract_new_customers: 'Attract New Customers',
  increase_sales: 'Increase Sales',
  fill_slow_periods: 'Fill Slow Periods',
};

const IMPACT_LABELS = {
  steady_growth: 'Steady Growth',
  quick_boost: 'Quick Boost',
  maximum_impact: 'Maximum Impact',
};

const IMPACT_TO_PROMPT = {
  steady_growth: 'Steady',
  quick_boost: 'Quick',
  maximum_impact: 'Max',
};

const DEFAULT_DRAFT = {
  entryPoint: 'B',
  goalKey: '',
  impactKey: '',
  offering: {
    itemType: '',
    itemName: '',
    itemDescription: '',
    price: '',
    categoryLabel: '',
    slowDays: [],
    slowTimes: [],
  },
  recommendations: [],
  selectedRecommendation: null,
  image: null,
  termsAccepted: false,

  step3Stage: 'pick', // 'pick' | 'review'
  theme: 'light', // 'light' | 'dark'
};

const extractAiText = res => {
  if (!res) return '';

  // if middleware returns string directly
  if (typeof res === 'string') return res;

  // common shapes
  const candidates = [
    res.text,
    res.result?.text,
    res.data?.text,
    res.payload?.text,
    res.response?.text,
    res.value,
  ];

  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
  }

  // sometimes text is nested differently
  if (typeof res.text?.value === 'string' && res.text.value.trim()) {
    return res.text.value.trim();
  }

  return '';
};

// legacy aiContent module callback wrapper
const dispatchGenerateText = (dispatch, id, params) =>
  dispatchAsAsync(aiActions.generateText(id, params), dispatch);

export default function SignupCouponBuilder({
  entryPoint = 'B',
  onRequireAuth,
  onGoToFullBuilder,
}) {
  const dispatch = useDispatch();

  const [step, setStep] = useState(1);

  // PERSISTED draft (only updated on submit/publish/edit/etc.)
  const [draft, setDraft] = useState(() => {
    const cached = loadJson(STORAGE_KEYS.DRAFT);
    return { ...DEFAULT_DRAFT, ...cached, entryPoint };
  });

  // PREVIEW state
  const [preview, setPreview] = useState(() => ({
    goalKey: draft.goalKey || '',
    impactKey: draft.impactKey || '',
    offering: { ...(draft.offering || DEFAULT_DRAFT.offering) },
  }));

  // keep preview initialized if draft was loaded from cache (once)
  const didInitPreviewRef = useRef(false);
  useEffect(() => {
    if (didInitPreviewRef.current) return;
    didInitPreviewRef.current = true;

    setPreview({
      goalKey: draft.goalKey || '',
      impactKey: draft.impactKey || '',
      offering: { ...(draft.offering || DEFAULT_DRAFT.offering) },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTheme = useCallback(() => {
    setDraft(prev => ({
      ...prev,
      theme: prev.theme === 'dark' ? 'light' : 'dark',
    }));
  }, []);

  // Persist draft
  useEffect(() => {
    saveJson(STORAGE_KEYS.DRAFT, draft);
  }, [draft]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    html.classList.add('signup-coupon-builder');
    body.classList.add('signup-coupon-builder');

    const isDark = draft.theme === 'dark';
    html.classList.toggle('signup-coupon-builder--dark', isDark);
    body.classList.toggle('signup-coupon-builder--dark', isDark);

    html.style.backgroundColor = isDark ? '#0b0f19' : '#ffffff';
    body.style.backgroundColor = isDark ? '#0b0f19' : '#ffffff';

    return () => {
      html.classList.remove('signup-coupon-builder');
      body.classList.remove('signup-coupon-builder');
      html.classList.remove('signup-coupon-builder--dark');
      body.classList.remove('signup-coupon-builder--dark');

      html.style.backgroundColor = '';
      body.style.backgroundColor = '';
    };
  }, [draft.theme]);

  const resetFlow = useCallback(() => {
    removeKey(STORAGE_KEYS.DRAFT);
    setDraft({ ...DEFAULT_DRAFT, entryPoint });
    setPreview({
      goalKey: '',
      impactKey: '',
      offering: { ...DEFAULT_DRAFT.offering },
    });
    setStep(1);
  }, [entryPoint]);

  /**
   * Step 1 -> Step 2
   */
  const handleGenerateFromStep1 = useCallback(
    payload => {
      setDraft(prev => ({
        ...prev,
        entryPoint,
        goalKey: payload.goalKey,
        impactKey: payload.impactKey,
        offering: payload.offering || prev.offering,
      }));

      setPreview({
        goalKey: payload.goalKey,
        impactKey: payload.impactKey,
        offering: payload.offering || { ...DEFAULT_DRAFT.offering },
      });

      setStep(2);
    },
    [entryPoint],
  );

  /**
   * Step2 calls: generateFn(draft) -> recommendations[]
   */
  const generateFn = useCallback(
    async currentDraft => {
      const goalLabel = GOAL_LABELS[currentDraft.goalKey] || '';
      const impactLabel = IMPACT_LABELS[currentDraft.impactKey] || '';
      const impactPromptValue =
        IMPACT_TO_PROMPT[currentDraft.impactKey] || 'Steady';

      if (!goalLabel) throw new Error('Please choose a goal.');
      if (!currentDraft.offering?.itemType)
        throw new Error('Please choose your offering type.');
      if (!(currentDraft.offering?.itemName || '').trim())
        throw new Error('Please enter an item/service name.');
      if (!impactLabel) throw new Error('Please choose a desired impact.');

      const itemName = (currentDraft.offering?.itemName || '').trim();
      const itemDescription = (
        currentDraft.offering?.itemDescription || ''
      ).trim();
      const priceNum = Number(currentDraft.offering?.price || 0) || 0;

      const impactKey =
        impactLabel === 'Maximum Impact'
          ? 'max'
          : impactLabel === 'Quick Boost'
          ? 'quick'
          : 'steady';

      const percentBalanced =
        impactKey === 'max' ? 25 : impactKey === 'quick' ? 20 : 15;
      const percentAggressive =
        impactKey === 'max' ? 35 : impactKey === 'quick' ? 30 : 20;

      const dollarBalanced = Math.max(
        5,
        Math.ceil(priceNum * (percentBalanced / 100)),
      );
      const dollarAggressive = Math.max(
        8,
        Math.ceil(priceNum * (percentAggressive / 100)),
      );

      const lowPrice =
        currentDraft.offering?.itemType === 'individual' &&
        priceNum > 0 &&
        priceNum < 50;

      const balanced =
        lowPrice || !priceNum
          ? {
              kind: 'percent',
              value: percentBalanced,
              label: `${percentBalanced}% off`,
            }
          : {
              kind: 'amount',
              value: dollarBalanced,
              label: `$${dollarBalanced} off`,
            };

      const aggressive =
        lowPrice || !priceNum
          ? {
              kind: 'percent',
              value: percentAggressive,
              label: `${percentAggressive}% off`,
            }
          : {
              kind: 'amount',
              value: dollarAggressive,
              label: `$${dollarAggressive} off`,
            };

      const points = {
        kind: 'points',
        value: impactKey === 'max' ? 2000 : impactKey === 'quick' ? 1000 : 500,
        label: `${
          impactKey === 'max' ? 2000 : impactKey === 'quick' ? 1000 : 500
        } points back`,
      };

      const base = [
        { id: 1, reward: balanced, type: 'Percent/Dollar Off' },
        { id: 2, reward: aggressive, type: 'High-Impact Deal' },
        { id: 3, reward: points, type: 'Points Back' },
      ];

      try {
        const results = await Promise.all(
          base.map(async card => {
            const couponType =
              card.reward.kind === 'percent'
                ? 'percentage'
                : card.reward.kind === 'amount'
                ? 'dollar'
                : 'points';

            const tPrompt = titlePrompt({
              businessName: 'Your Business',
              city: 'Your City',
              couponType,
              discountValue: card.reward.value,
              tone: card.id === 2 ? 'excited' : 'friendly',

              itemName: itemName || 'Your Offer',
              itemDescription: itemDescription || '',
              goal: goalLabel,

              desiredImpact: impactPromptValue,

              slowDays: currentDraft.offering?.slowDays || [],
              slowTimes: currentDraft.offering?.slowTimes || [],
              itemType: currentDraft.offering?.itemType || '',
              rules: '36 chars max; avoid placeholders; must be specific.',
            });

            const dPrompt = descriptionPrompt({
              businessName: 'Your Business',
              city: 'Your City',
              couponType,
              discountValue: card.reward.value,
              tone: card.id === 2 ? 'excited' : 'friendly',

              itemName: itemName || 'Your Offer',
              itemDescription: itemDescription || '',

              desiredImpact: impactPromptValue,

              slowDays: currentDraft.offering?.slowDays || [],
              slowTimes: currentDraft.offering?.slowTimes || [],
              itemType: currentDraft.offering?.itemType || '',
              rules: '≤200 chars. No placeholders.',
            });

            const [tRes, dRes] = await Promise.all([
              dispatchGenerateText(dispatch, `mini_title_${card.id}`, {
                prompt: tPrompt,
                type: 'title',
                length: 36,
              }),
              dispatchGenerateText(dispatch, `mini_desc_${card.id}`, {
                prompt: dPrompt,
                type: 'description',
                length: 200,
              }),
            ]);

            return {
              id: card.id,
              type: card.type,
              headline:
                (tRes && tRes.text) ||
                `${card.reward.label} on ${itemName}`.slice(0, 36),
              details:
                (dRes && dRes.text) ||
                'Limited-time deal. Tap to redeem in the app.',
              reward: card.reward,
            };
          }),
        );

        return results;
      } catch (e) {
        toast.error('AI failed — using fallback recommendations.');
        return base.map(card => ({
          id: card.id,
          type: card.type,
          headline: `${card.reward.label} on ${itemName || 'your offer'}`.slice(
            0,
            36,
          ),
          details: 'Limited-time deal. Tap to redeem in the app.',
          reward: card.reward,
        }));
      }
    },
    [dispatch],
  );

  const handleStep2Success = useCallback(recs => {
    setDraft(prev => ({
      ...prev,
      recommendations: recs,
      selectedRecommendation: recs?.[0] || null,
      step3Stage: 'pick',
    }));
    setStep(3);
  }, []);

  const handleSetStep3Stage = useCallback(stage => {
    setDraft(prev => ({ ...prev, step3Stage: stage }));
  }, []);

  const handleSelectRecommendation = useCallback(rec => {
    setDraft(prev => ({ ...prev, selectedRecommendation: rec }));
  }, []);

  const handleSetImage = useCallback(urlOrNull => {
    setDraft(prev => ({ ...prev, image: urlOrNull }));
  }, []);

  const handleToggleTerms = useCallback(checked => {
    setDraft(prev => ({ ...prev, termsAccepted: checked }));
  }, []);

  const handlePublish = useCallback(() => {
    if (!draft.termsAccepted) return;

    saveJson(STORAGE_KEYS.DRAFT, draft);

    toast.success('✅ Your coupon has been saved in drafts!', {
      autoClose: 2200,
    });

    onRequireAuth && onRequireAuth();
  }, [draft, onRequireAuth]);

  const handleEditCoupon = useCallback(() => {
    saveJson(STORAGE_KEYS.DRAFT, draft);
    onGoToFullBuilder && onGoToFullBuilder();
  }, [draft, onGoToFullBuilder]);

  const previewHeadline = useMemo(() => {
    if (step === 1)
      return (preview.offering?.itemName || '').trim() || 'Your Coupon Title';
    return draft.selectedRecommendation?.headline || 'Your Coupon Title';
  }, [step, preview.offering?.itemName, draft.selectedRecommendation]);

  const previewDetails = useMemo(() => {
    if (step === 1)
      return (
        (preview.offering?.itemDescription || '').trim() ||
        'Build a coupon and preview it here.'
      );
    return (
      draft.selectedRecommendation?.details ||
      'Build a coupon and preview it here.'
    );
  }, [step, preview.offering?.itemDescription, draft.selectedRecommendation]);

  const previewDiscount = useMemo(() => {
    if (step === 1) return '';
    return draft.selectedRecommendation?.reward?.label || '';
  }, [step, draft.selectedRecommendation]);

  return (
    <div
      className={`${styles.page} ${draft.theme === 'dark' ? styles.dark : ''}`}
    >
      <div className={styles.columns}>
        <div className={styles.leftCol}>
          <div className={styles.topBar}>
            <div className={styles.stepper}>
              <div className={styles.stepLabel}>
                <span className={styles.stepKicker}>STEP {step} OF 3</span>
                <span className={styles.stepTitle}>
                  {step === 1
                    ? 'Goal & Offering'
                    : step === 2
                    ? 'Building Coupons'
                    : 'Final Review'}
                </span>
              </div>

              <div className={styles.track}>
                <div
                  className={styles.fill}
                  style={{
                    width: step === 1 ? '28%' : step === 2 ? '62%' : '100%',
                  }}
                />
              </div>
            </div>

            <div className={styles.topActions}>
              <button
                type="button"
                className={styles.themeToggle}
                onClick={toggleTheme}
                aria-label="Toggle dark mode"
                title={
                  draft.theme === 'dark'
                    ? 'Switch to light mode'
                    : 'Switch to dark mode'
                }
              >
                <span className={styles.toggleKnob} />
                <span className={styles.toggleText}>
                  {draft.theme === 'dark' ? 'Dark' : 'Light'}
                </span>
              </button>

              <button
                className={styles.resetBtn}
                onClick={resetFlow}
                type="button"
              >
                Reset
              </button>
            </div>
          </div>

          <div className={styles.stepStage} key={step}>
            {step === 1 && (
              <Step1_CombinedSetup
                entryPoint={entryPoint}
                draft={draft}
                onGenerate={handleGenerateFromStep1}
                onSkipForNow={null}
                onPreviewChange={setPreview}
                onGenerateTitle={async payload => {
                  const goalLabel = GOAL_LABELS[payload.goalKey] || '';
                  const impactPromptValue =
                    IMPACT_TO_PROMPT[payload.impactKey] || 'Steady';

                  const tPrompt = titlePrompt({
                    businessName: 'Your Business',
                    city: 'Your City',
                    couponType: 'percentage',
                    discountValue: 20,
                    tone: 'friendly',
                    itemName: payload.offering?.itemName || 'Your Offer',
                    itemDescription: payload.offering?.itemDescription || '',
                    goal: goalLabel,
                    desiredImpact: impactPromptValue,
                    slowDays: payload.offering?.slowDays || [],
                    slowTimes: payload.offering?.slowTimes || [],
                    itemType: payload.offering?.itemType || '',
                    rules:
                      '36 chars max; avoid placeholders; must be specific.',
                  });

                  const res = await dispatchGenerateText(
                    dispatch,
                    'mini_step1_title',
                    {
                      prompt: tPrompt,
                      type: 'title',
                      length: 36,
                    },
                  );

                  const text = extractAiText(res);

                  // if (!text) {
                  //   // eslint-disable-next-line no-console
                  //   console.log('[AI DEBUG] title response:', res);
                  // }

                  return text;
                }}
                onGenerateDescription={async payload => {
                  const impactPromptValue =
                    IMPACT_TO_PROMPT[payload.impactKey] || 'Steady';

                  const dPrompt = descriptionPrompt({
                    businessName: 'Your Business',
                    city: 'Your City',
                    couponType: 'percentage',
                    discountValue: 20,
                    tone: 'friendly',
                    itemName: payload.offering?.itemName || 'Your Offer',
                    itemDescription: payload.offering?.itemDescription || '',
                    desiredImpact: impactPromptValue,
                    slowDays: payload.offering?.slowDays || [],
                    slowTimes: payload.offering?.slowTimes || [],
                    itemType: payload.offering?.itemType || '',
                    currentDescription: payload.offering?.itemDescription || '',
                    rules: '≤200 chars. No placeholders.',
                  });

                  const res = await dispatchGenerateText(
                    dispatch,
                    'mini_step1_desc',
                    {
                      prompt: dPrompt,
                      type: 'description',
                      length: 200,
                    },
                  );

                  const text = extractAiText(res);

                  // if (!text) {
                  //   // eslint-disable-next-line no-console
                  //   console.log('[AI DEBUG] description response:', res);
                  // }

                  return text;
                }}
              />
            )}

            {step === 2 && (
              <Step2_LoadingRecommendations
                draft={draft}
                onBack={() => setStep(1)}
                onSuccess={handleStep2Success}
                onFailGoToScratch={() =>
                  onGoToFullBuilder && onGoToFullBuilder()
                }
                generateFn={generateFn}
              />
            )}

            {step === 3 && (
              <Step3_FinalReview
                draft={draft}
                onBack={() => setStep(1)}
                onSelectRecommendation={handleSelectRecommendation}
                onSetImage={handleSetImage}
                onToggleTerms={handleToggleTerms}
                onPublish={handlePublish}
                onEditCoupon={handleEditCoupon}
                onSetStage={handleSetStep3Stage}
              />
            )}
          </div>
        </div>

        <aside className={styles.rightCol}>
          <PhonePreview
            image={draft.image || null}
            businessName="Your Business"
            city="Your City"
            title={previewHeadline}
            description={previewDetails}
            discountValue={previewDiscount}
            showCTA={step >= 3}
          />
        </aside>
      </div>
    </div>
  );
}

SignupCouponBuilder.propTypes = {
  entryPoint: PropTypes.oneOf(['A', 'B']),
  onRequireAuth: PropTypes.func,
  onGoToFullBuilder: PropTypes.func,
};
